import { HStack } from '@talos/kyoko';
import { useCallback, useEffect, useRef } from 'react';
import { AggregatedMetricSelect } from '../AggregatedMetric/AggregatedMetricSelect';
import {
  METRIC_CONTROL_WIDTH,
  resolveSignalsTooltipFormatter,
  resolveSignalsValueFormatter,
  SIGNALS_DEFAULT_TIME_RANGE,
  SignalsCurrencySelect,
  SignalsLineChart,
  SignalsModuleShell,
  SignalsTimeRangeControl,
  type SignalsTimeRange,
} from '../core';
import { DEFAULT_METRIC, DEFAULT_METRIC_FULL_NAME, useMarketCapData } from './useMarketCapData';

const DEFAULT_SYMBOL = 'BTC';

export interface SingleAssetMetricModuleProps {
  /**
   * Current asset selection (Talos `Currency.Symbol`). When undefined, the module falls back to
   * the space asset, then to the default. The parent owns persistence — every change is reported
   * via `onSymbolChange`, including reseeding/validation transitions.
   */
  symbol?: string;
  /** Current metric selection (CoinMetrics metric ID). Undefined falls back to `DEFAULT_METRIC`. */
  metric?: string;
  /** Current time-range selection. Undefined falls back to `SIGNALS_DEFAULT_TIME_RANGE`. */
  timeRange?: SignalsTimeRange;
  /**
   * Asset filter set at the space level. When defined, the module follows it on each
   * change. The user can still pick a different asset locally via the in-panel selector;
   * that local choice persists until the next time `spaceAsset` changes to a defined value.
   */
  spaceAsset?: string;
  /** Called whenever the active symbol changes (user selection, space-asset transition, or fallback). */
  onSymbolChange: (symbol: string) => void;
  /** Called whenever the user picks a different metric or it resets to the default due to asset change. */
  onMetricChange: (metric: string, fullMetricName: string) => void;
  /** Called whenever the user picks a different time range. */
  onTimeRangeChange: (timeRange: SignalsTimeRange) => void;
}

export function SingleAssetMetricModule({
  symbol,
  metric,
  timeRange,
  spaceAsset,
  onSymbolChange,
  onMetricChange,
  onTimeRangeChange,
}: SingleAssetMetricModuleProps) {
  // Effective values used for fetching/rendering when the parent hasn't yet supplied one.
  const effectiveSymbol = symbol ?? spaceAsset;
  const effectiveTimeRange = timeRange ?? SIGNALS_DEFAULT_TIME_RANGE;
  const effectiveMetric = metric ?? DEFAULT_METRIC;

  const {
    series,
    isFetching,
    isError,
    talosSymbols,
    isAssetsLoading,
    availableMetrics,
    isMetricsLoading,
    isMetricsError,
    metricFullName,
    getMetricFullName,
  } = useMarketCapData(effectiveSymbol, effectiveMetric, effectiveTimeRange);

  // Reseed selection on each transition of `spaceAsset` to a defined value.
  // Clearing the space asset (defined -> undefined) preserves the current selection.
  const prevSpaceAssetRef = useRef(spaceAsset);
  useEffect(() => {
    if (spaceAsset !== prevSpaceAssetRef.current) {
      prevSpaceAssetRef.current = spaceAsset;
      if (spaceAsset != null && spaceAsset !== symbol) {
        onSymbolChange(spaceAsset);
      }
    }
  }, [spaceAsset, symbol, onSymbolChange]);

  // Once the asset list resolves, ensure the current selection is valid.
  // If nothing has been picked or the picked symbol isn't supported, fall back to
  // the first available symbol (preferring spaceAsset → DEFAULT_SYMBOL → first listed).
  useEffect(() => {
    if (isAssetsLoading || talosSymbols.length === 0) {
      return;
    }
    if (symbol != null && talosSymbols.includes(symbol)) {
      return;
    }
    const fallback =
      (spaceAsset != null && talosSymbols.includes(spaceAsset) && spaceAsset) ||
      (talosSymbols.includes(DEFAULT_SYMBOL) && DEFAULT_SYMBOL) ||
      talosSymbols[0];
    onSymbolChange(fallback);
  }, [isAssetsLoading, talosSymbols, symbol, spaceAsset, onSymbolChange]);

  // When availableMetrics resolves for the selected asset, validate the current metric.
  // Prefer DEFAULT_METRIC on reset; fall back to the first available metric if the asset
  // doesn't support DEFAULT_METRIC either (prevents an infinite reset loop).
  useEffect(() => {
    if (isMetricsLoading || availableMetrics.length === 0) {
      return;
    }
    if (availableMetrics.includes(effectiveMetric)) {
      return;
    }
    const fallback = availableMetrics.includes(DEFAULT_METRIC) ? DEFAULT_METRIC : availableMetrics[0];
    const fallbackFullName = fallback === DEFAULT_METRIC ? DEFAULT_METRIC_FULL_NAME : getMetricFullName(fallback);
    onMetricChange(fallback, fallbackFullName);
  }, [isMetricsLoading, availableMetrics, effectiveMetric, onMetricChange, getMetricFullName]);

  // Sync the resolved metric display name to the parent whenever effectiveMetric or
  // metricFullName changes. This keeps the panel title correct when a persisted metric
  // is already valid on mount (the metric-validation effect bails early and never calls
  // onMetricChange, so fullMetricNameRef in the panel would otherwise stay stale).
  const reportedMetricRef = useRef<{ metric: string; fullName: string } | null>(null);
  useEffect(() => {
    const reported = reportedMetricRef.current;
    if (reported?.metric === effectiveMetric && reported?.fullName === metricFullName) {
      return;
    }
    reportedMetricRef.current = { metric: effectiveMetric, fullName: metricFullName };
    onMetricChange(effectiveMetric, metricFullName);
  }, [effectiveMetric, metricFullName, onMetricChange]);

  // Track the last symbol whose data finished loading so we can distinguish an
  // asset change (show loader — chart would show the wrong asset's data) from a
  // time range change on the same asset (keep stale data — smooth transition).
  const prevSymbolRef = useRef(effectiveSymbol);
  const isNewAssetFetching = isFetching && prevSymbolRef.current !== effectiveSymbol;
  if (!isFetching) {
    prevSymbolRef.current = effectiveSymbol;
  }

  const isLoading = isAssetsLoading || isNewAssetFetching || (isFetching && series.length === 0);
  const isControlsDisabled = isAssetsLoading || isFetching;
  const isTimeRangeDisabled = isControlsDisabled || isError;
  const formatSeriesName = useCallback(() => metricFullName, [metricFullName]);

  const handleMetricSelect = useCallback(
    (nextMetric: string) => {
      onMetricChange(nextMetric, getMetricFullName(nextMetric));
    },
    [getMetricFullName, onMetricChange]
  );

  return (
    <SignalsModuleShell
      header={
        <HStack gap="spacing8">
          <SignalsCurrencySelect
            options={talosSymbols}
            value={effectiveSymbol}
            onChange={next => {
              if (next != null) {
                onSymbolChange(next);
              }
            }}
            disabled={isControlsDisabled}
            prefixLabel="Asset"
          />
          <AggregatedMetricSelect
            metric={effectiveMetric}
            availableMetrics={availableMetrics}
            isLoading={isMetricsLoading}
            isError={isMetricsError}
            hasSelectedAssets={effectiveSymbol != null}
            onMetricChange={handleMetricSelect}
            getLabel={getMetricFullName}
            testID="single-asset-metric-select"
            prefix="Metric"
            disabled={isControlsDisabled}
            width={METRIC_CONTROL_WIDTH}
          />
        </HStack>
      }
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load metric data."
      controls={
        <SignalsTimeRangeControl
          value={effectiveTimeRange}
          onChange={onTimeRangeChange}
          disabled={isTimeRangeDisabled}
        />
      }
    >
      <SignalsLineChart
        series={series}
        formatValue={resolveSignalsValueFormatter(effectiveMetric)}
        formatTooltipValue={resolveSignalsTooltipFormatter(effectiveMetric)}
        formatSeriesName={formatSeriesName}
      />
    </SignalsModuleShell>
  );
}
